"use client";

import { Time } from "@commerce/components/time";
import { useCopy } from "@commerce/components/use-copy";
import { errorMessage } from "@saas/settings/lib/errors";
import { orpc } from "@shared/lib/orpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import { DisabledReason } from "@ui/components/info-tip";
import { AnchorLinkButton, LinkButton } from "@ui/components/link-button";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@ui/components/sheet";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ui/components/tabs";
import { toast } from "@ui/components/toast";
import { cn } from "@ui/lib";
import {
	CheckIcon,
	CopyIcon,
	ExternalLinkIcon,
	LinkIcon,
	MessagesSquareIcon,
	RotateCcwIcon,
	TerminalIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { JsonView } from "./JsonView";
import {
	formatDuration,
	type LogDetail,
	OUTCOME_META,
	resultOf,
	SOURCE_LABEL,
} from "./meta";

/** One log row (`?id=`): what happened, what to do, its request and response, related steps. */
export function LogSheet({
	organizationId,
	logId,
	onClose,
	onOpen,
}: {
	organizationId: string;
	logId: string | null;
	onClose: () => void;
	/** Opens a related row from the timeline. */
	onOpen: (logId: string) => void;
}) {
	return (
		<Sheet
			open={logId !== null}
			onOpenChange={(open) => {
				if (!open) {
					onClose();
				}
			}}
		>
			<SheetContent
				side="right"
				className="gap-0 data-[side=right]:w-full data-[side=right]:sm:max-w-2xl"
			>
				{logId ? (
					<LogDetailView
						key={logId}
						organizationId={organizationId}
						logId={logId}
						onOpen={onOpen}
					/>
				) : null}
			</SheetContent>
		</Sheet>
	);
}

function LogDetailView({
	organizationId,
	logId,
	onOpen,
}: {
	organizationId: string;
	logId: string;
	onOpen: (logId: string) => void;
}) {
	const queryClient = useQueryClient();
	const { organizationSlug } = useParams({ strict: false });
	const query = useQuery(
		orpc.logs.get.queryOptions({ input: { organizationId, logId } }),
	);
	const replay = useMutation(
		orpc.logs.replay.mutationOptions({
			onSuccess: (result) => {
				toast.add({
					type: result.outcome === "OK" ? "success" : "warning",
					title:
						result.outcome === "OK"
							? "Sent again"
							: "Sent again, and it failed again",
					description: "The new attempt is the newest row.",
				});
				void queryClient.invalidateQueries({
					queryKey: orpc.logs.key(),
				});
				onOpen(result.logId);
			},
			onError: (error) =>
				toast.add({
					type: "error",
					title: "Not sent again",
					description: errorMessage(error),
				}),
		}),
	);
	const log = query.data;
	const link = useCopy(
		typeof window === "undefined"
			? ""
			: `${window.location.origin}${window.location.pathname}?id=${encodeURIComponent(logId)}`,
	);

	if (query.isError) {
		return (
			<SheetHeader className="border-border/70 border-b pr-14">
				<SheetTitle>Log row not found</SheetTitle>
				<SheetDescription>
					{errorMessage(
						query.error,
						"It may be older than the logs keep (30 days).",
					)}
				</SheetDescription>
			</SheetHeader>
		);
	}
	if (!log) {
		return (
			<div className="flex flex-col gap-4 p-5 md:p-6" aria-busy>
				<Skeleton className="h-6 w-56" />
				<Skeleton className="h-4 w-40" />
				<Skeleton className="h-20 w-full rounded-2xl" />
				<Skeleton className="h-48 w-full rounded-2xl" />
			</div>
		);
	}

	const outcome = OUTCOME_META[log.outcome];
	return (
		<>
			<SheetHeader className="border-border/70 border-b pr-14">
				<SheetTitle className="flex flex-wrap items-center gap-2">
					{SOURCE_LABEL[log.source]}
					<span className="font-mono font-normal text-muted-foreground text-sm">
						{log.type}
					</span>
				</SheetTitle>
				<SheetDescription className="flex flex-wrap items-center gap-2">
					<Badge variant={outcome.variant}>{outcome.label}</Badge>
					<span className="tabular">{resultOf(log)}</span>
					<span aria-hidden className="max-sm:hidden">
						·
					</span>
					<Time at={new Date(log.createdAt)} as="full" />
				</SheetDescription>
				<div className="flex flex-wrap gap-2 pt-1">
					<Button variant="outline" size="sm" onClick={link.copy}>
						{link.copied ? (
							<CheckIcon data-icon="inline-start" />
						) : (
							<LinkIcon data-icon="inline-start" />
						)}
						Copy link
					</Button>
					{log.conversationId && organizationSlug ? (
						<LinkButton
							variant="outline"
							size="sm"
							to="/app/$organizationSlug/conversations"
							params={{ organizationSlug }}
							search={{ id: log.conversationId }}
						>
							<MessagesSquareIcon data-icon="inline-start" />
							Open conversation
						</LinkButton>
					) : null}
				</div>
			</SheetHeader>

			<div className="min-h-0 flex-1 overflow-y-auto">
				<div className="flex flex-col gap-5 p-5 md:p-6">
					<Explanation log={log} />
					<Ids log={log} />
					{log.timeline.length > 1 ? (
						<Timeline log={log} onOpen={onOpen} />
					) : null}
					<Payloads log={log} />
				</div>
			</div>

			<div className="flex flex-wrap items-center gap-2 border-border/70 border-t px-5 py-3 md:px-6">
				<DisabledReason
					reason={
						log.replayable
							? null
							: "Only a failed send can go again: never a placement, a call step or a webhook that worked"
					}
				>
					<Button
						size="sm"
						disabled={!log.replayable || replay.isPending}
						onClick={() =>
							replay.mutate({ organizationId, logId: log.id })
						}
					>
						{replay.isPending ? (
							<Spinner data-icon="inline-start" />
						) : (
							<RotateCcwIcon data-icon="inline-start" />
						)}
						Send again
					</Button>
				</DisabledReason>
			</div>
		</>
	);
}

/** What happened in a sentence, and what to do about it. */
function Explanation({ log }: { log: LogDetail }) {
	const what =
		log.explanation?.title ??
		log.summary ??
		log.errorTitle ??
		(log.outcome === "OK" ? "It worked." : OUTCOME_META[log.outcome].label);
	return (
		<dl className="grid gap-3 sm:grid-cols-[8rem_minmax(0,1fr)]">
			<Fact label="What happened">
				<span className="text-pretty">
					{log.errorCode && log.outcome === "FAILED" ? (
						<>
							Meta refused it: {what} ({log.errorCode}).
						</>
					) : (
						what
					)}
				</span>
			</Fact>
			{log.explanation ? (
				<Fact label="What to do">
					<span className="flex flex-col items-start gap-1.5">
						<span className="text-pretty">
							{log.explanation.action}
						</span>
						{log.explanation.docUrl ? (
							<AnchorLinkButton
								href={log.explanation.docUrl}
								target="_blank"
								rel="noreferrer"
								variant="link"
								size="sm"
								className="h-auto px-0"
							>
								Meta's page on this error
								<ExternalLinkIcon data-icon="inline-end" />
							</AnchorLinkButton>
						) : null}
					</span>
				</Fact>
			) : null}
			{log.durationMs != null ? (
				<Fact label="Took">
					<span className="tabular">
						{formatDuration(log.durationMs)}
					</span>
				</Fact>
			) : null}
		</dl>
	);
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className="contents">
			<dt className="text-muted-foreground text-sm">{label}</dt>
			<dd className="min-w-0 text-sm">{children}</dd>
		</div>
	);
}

/** The ids a developer pastes elsewhere: message, call, order. */
function Ids({ log }: { log: LogDetail }) {
	const ids = [
		["Message id", log.wamid],
		["Call id", log.callId],
		["Order", log.orderId],
	].filter((entry): entry is [string, string] => Boolean(entry[1]));
	if (ids.length === 0) {
		return null;
	}
	return (
		<dl className="grid gap-2 sm:grid-cols-[8rem_minmax(0,1fr)]">
			{ids.map(([label, value]) => (
				<Fact key={label} label={label}>
					<CopyId value={value} />
				</Fact>
			))}
		</dl>
	);
}

function CopyId({ value }: { value: string }) {
	const { copied, copy } = useCopy(value);
	return (
		<span className="flex min-w-0 items-center gap-1">
			<span className="min-w-0 truncate font-mono text-xs">{value}</span>
			<Button
				variant="ghost"
				size="icon-xs"
				onClick={copy}
				aria-label={copied ? "Copied" : `Copy ${value}`}
			>
				{copied ? <CheckIcon /> : <CopyIcon />}
			</Button>
		</span>
	);
}

/** Every row about the same message, call or order, oldest first; this one is marked. */
function Timeline({
	log,
	onOpen,
}: {
	log: LogDetail;
	onOpen: (logId: string) => void;
}) {
	return (
		<section className="flex flex-col gap-2">
			<h3 className="font-medium text-sm">Timeline</h3>
			<ol className="flex flex-col gap-1 border-border border-s ps-3">
				{log.timeline.map((step) => {
					const current = step.id === log.id;
					return (
						<li key={step.id}>
							<button
								type="button"
								disabled={current}
								onClick={() => onOpen(step.id)}
								className={cn(
									"flex w-full flex-wrap items-center gap-x-2 gap-y-0.5 rounded-xl px-2 py-1 text-left text-xs outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/40 disabled:cursor-default",
									current && "bg-muted font-medium",
								)}
							>
								<Time
									at={new Date(step.createdAt)}
									className="tabular"
								/>
								<span className="font-mono">{step.type}</span>
								<Badge
									variant={OUTCOME_META[step.outcome].variant}
								>
									{OUTCOME_META[step.outcome].label}
								</Badge>
								{step.errorCode ? (
									<span className="tabular text-muted-foreground">
										{step.errorCode}
									</span>
								) : null}
							</button>
						</li>
					);
				})}
			</ol>
		</section>
	);
}

/** Request and response as JSON trees, with copy as JSON or as a cURL command. */
function Payloads({ log }: { log: LogDetail }) {
	const request = useCopy(JSON.stringify(log.request ?? null, null, 2));
	const response = useCopy(JSON.stringify(log.response ?? null, null, 2));
	const curl = useCopy(log.curl ?? "");
	return (
		<Tabs defaultValue="request" className="gap-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<TabsList>
					<TabsTrigger value="request" className="px-3">
						Request
					</TabsTrigger>
					<TabsTrigger value="response" className="px-3">
						Response
					</TabsTrigger>
				</TabsList>
				{log.curl ? (
					<Button variant="outline" size="sm" onClick={curl.copy}>
						{curl.copied ? (
							<CheckIcon data-icon="inline-start" />
						) : (
							<TerminalIcon data-icon="inline-start" />
						)}
						Copy as cURL
					</Button>
				) : null}
			</div>
			<TabsContent value="request" className="flex flex-col gap-2">
				<JsonView value={log.request} />
				<Button
					variant="ghost"
					size="sm"
					className="self-start"
					onClick={request.copy}
				>
					{request.copied ? (
						<CheckIcon data-icon="inline-start" />
					) : (
						<CopyIcon data-icon="inline-start" />
					)}
					Copy JSON
				</Button>
			</TabsContent>
			<TabsContent value="response" className="flex flex-col gap-2">
				<JsonView value={log.response} />
				<Button
					variant="ghost"
					size="sm"
					className="self-start"
					onClick={response.copy}
				>
					{response.copied ? (
						<CheckIcon data-icon="inline-start" />
					) : (
						<CopyIcon data-icon="inline-start" />
					)}
					Copy JSON
				</Button>
			</TabsContent>
			{log.curl ? (
				<pre className="overflow-x-auto rounded-2xl bg-muted/50 p-3 font-mono text-xs">
					{log.curl}
				</pre>
			) : null}
		</Tabs>
	);
}
